// Gmail-style compose — new mail, reply, reply-all, forward.
// Rich HTML body + optional signature from the connected mailbox.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { Send, X, Loader2, AlertCircle, Paperclip, FileText, ChevronUp, Sparkles, Clock, FolderOpen, Check } from "lucide-react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Modal from "./Modal";
import { Button } from "../atoms";
import EmailChipInput from "../molecules/EmailChipInput";
import EmailComposeEditor from "../molecules/EmailComposeEditor";
import EmailAssistPanel from "../organisms/EmailAssistPanel";
import EmailDraftPanel from "../organisms/EmailDraftPanel";
import { useEmailAccounts, useSendEmail, useScheduleEmail } from "../hooks/useEmails";
import { useLeadDocuments } from "../hooks/useLeads";
import { crmApi } from "../api/client";
import {
  appendSignature, stripHtml, buildForwardQuote, fwdSubject, replyAllRecipients,
  textToComposeHtml,
} from "../utils/emailComposeUtils";
import "../molecules/EmailComposeEditor.css";
import "./EmailComposeModal.css";

const MAX_TOTAL_BYTES = 22 * 1024 * 1024;

const MODE_LABEL = {
  new: "New message",
  reply: "Reply",
  replyAll: "Reply all",
  forward: "Forward",
};

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentChip({ file, onRemove }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "6px 8px 6px 10px",
      background: "var(--color-bg-muted)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8, fontSize: 12, maxWidth: 240,
    }}>
      <FileText size={13} style={{ color: "var(--crm-primary)" }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
        {file.name}
      </span>
      <span style={{ color: "var(--color-text-secondary)", fontSize: 11 }}>{fmtBytes(file.size)}</span>
      <button type="button" onClick={onRemove} aria-label={`Remove ${file.name}`} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}>
        <X size={12} />
      </button>
    </div>
  );
}

function FieldRow({ label, children, extra }) {
  return (
    <div className="crm-compose-field-row">
      <div className="crm-compose-field-label">{label}</div>
      <div className="crm-compose-field-control">
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {extra}
      </div>
    </div>
  );
}

function SignaturePreview({ html }) {
  if (!html?.trim()) return null;
  return (
    <div className="crm-compose-signature-preview">
      <div className="crm-compose-signature-preview__label">Signature</div>
      <div
        className="crm-compose-signature-preview__body"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// ─── Lead file picker overlay ─────────────────────────────────────────────────
// Shows files already uploaded to the lead so user can attach them to email
// without re-uploading. Streams file bytes through the backend proxy to avoid CORS.

function LeadFilePicker({ leadId, open, onClose, onAttach }) {
  const { data, isLoading } = useLeadDocuments(open ? leadId : null);
  const docs = data?.documents || [];
  const [selected, setSelected] = useState(new Set());
  const [fetching, setFetching] = useState(false);

  // Reset selection when picker opens
  useEffect(() => { if (open) setSelected(new Set()); }, [open]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleAttach = async () => {
    if (!selected.size) return;
    setFetching(true);
    try {
      const files = await Promise.all(
        [...selected].map(async (docId) => {
          const doc = docs.find((d) => String(d._id) === docId);
          // Use the backend proxy (/stream) — avoids CORS when fetching S3 directly.
          const resp = await crmApi.get(`/leads/${leadId}/documents/${docId}/stream`, { responseType: "blob" });
          const blob = resp.data;
          return new File([blob], doc?.filename || "file", { type: doc?.mimeType || blob.type });
        })
      );
      onAttach(files);
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to load file");
    } finally {
      setFetching(false);
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 440, maxWidth: "calc(100vw - 32px)", maxHeight: "70vh", background: "var(--color-bg-base)", borderRadius: 8, border: "1px solid var(--color-border-subtle)", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--color-border-subtle)", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: 7 }}>
            <FolderOpen size={15} style={{ color: "var(--crm-primary)" }} /> Attach from lead files
          </div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", display: "flex" }}>
            <X size={15} />
          </button>
        </div>

        {/* File list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {isLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 20, justifyContent: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
              <Loader2 size={13} className="animate-spin" /> Loading files…
            </div>
          )}
          {!isLoading && docs.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--color-text-secondary)" }}>
              No files uploaded to this lead yet.
            </div>
          )}
          {!isLoading && docs.map((doc) => {
            const id = String(doc._id);
            const isSelected = selected.has(id);
            return (
              <div
                key={id}
                onClick={() => toggle(id)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", background: isSelected ? "var(--crm-primary-soft)" : "transparent" }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "var(--color-bg-muted)"; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isSelected ? "var(--crm-primary)" : "var(--color-border-subtle)"}`, background: isSelected ? "var(--crm-primary)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 120ms" }}>
                  {isSelected && <Check size={11} color="#fff" strokeWidth={3} />}
                </div>
                <FileText size={14} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.filename}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtBytes(doc.sizeBytes)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--color-border-subtle)", flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1 }}>
            {selected.size > 0 ? `${selected.size} file${selected.size > 1 ? "s" : ""} selected` : "Select files to attach"}
          </span>
          <button type="button" onClick={onClose} style={{ padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "transparent", border: "1px solid var(--color-border-subtle)", borderRadius: 7, cursor: "pointer", color: "var(--color-text-secondary)" }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAttach}
            disabled={!selected.size || fetching}
            style={{ padding: "6px 14px", fontSize: 12, fontWeight: 600, background: "var(--crm-primary)", border: "none", borderRadius: 7, cursor: selected.size && !fetching ? "pointer" : "not-allowed", color: "#fff", opacity: selected.size && !fetching ? 1 : 0.5, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {fetching ? <><Loader2 size={12} className="animate-spin" /> Attaching…</> : <><Paperclip size={12} /> Attach</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EmailComposeModal({
  open,
  onClose,
  defaultTo = "",
  defaultSubject = "",
  leadId,
  mode = "new",
  replyTo,
}) {
  const { data: accounts = [], isLoading: accountsLoading } = useEmailAccounts();
  const send = useSendEmail();
  const schedule = useScheduleEmail();
  const fileInputRef = useRef(null);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [filePickerOpen, setFilePickerOpen] = useState(false);

  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.status === "active"),
    [accounts],
  );

  const [accountId, setAccountId] = useState("");
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [subject, setSubject] = useState("");
  const [messageHtml, setMessageHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(true);
  // Text typed into a recipient box but never turned into a chip. It is not in
  // `to`/`cc`/`bcc`, so without this the address is silently dropped on Send.
  const [drafts, setDrafts] = useState({ to: "", cc: "", bcc: "" });
  const setDraftFor = (field) => (text) =>
    setDrafts((d) => (d[field] === text ? d : { ...d, [field]: text }));
  const pendingDraft = ["to", "cc", "bcc"].find((f) => drafts[f].trim());

  const selectedAccount = useMemo(
    () => activeAccounts.find((a) => String(a._id) === String(accountId)),
    [activeAccounts, accountId],
  );

  const signatureHtml = selectedAccount?.signatureHtml || "";

  useEffect(() => {
    if (!open) return;
    setError("");
    setAttachments([]);
    setShowCc(false);
    setShowBcc(false);
    setIncludeSignature(true);
    setDrafts({ to: "", cc: "", bcc: "" });

    const accId = replyTo?.accountId || activeAccounts[0]?._id || "";
    setAccountId(String(accId));

    setScheduledAt(null);
    setShowSchedule(false);

    if (mode === "forward" && replyTo?.message) {
      const msg = replyTo.message;
      setTo([]);
      setCc([]);
      setBcc([]);
      setSubject(fwdSubject(msg.subject));
      setMessageHtml(buildForwardQuote(msg));
      setShowCc(false);
      setShowBcc(false);
      return;
    }

    if (replyTo && (mode === "reply" || mode === "replyAll")) {
      const acc = activeAccounts.find((a) => String(a._id) === String(accId));
      const accEmail = replyTo.accountEmail || acc?.email || "";
      if (mode === "replyAll") {
        const { to: toList, cc: ccList } = replyAllRecipients(replyTo, accEmail);
        setTo(toList);
        setCc(ccList);
        setShowCc(ccList.length > 0);
      } else {
        setTo([replyTo.fromAddress].filter(Boolean));
        setCc([]);
      }
      setBcc([]);
      const subj = replyTo.subject?.toLowerCase().startsWith("re:")
        ? replyTo.subject
        : `Re: ${replyTo.subject || ""}`;
      setSubject(subj);
      setMessageHtml("");
      return;
    }

    setTo(defaultTo ? [defaultTo] : []);
    setCc([]);
    setBcc([]);
    setSubject(defaultSubject);
    setMessageHtml("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, replyTo?.replyTo?.providerMessageId, replyTo?.message?.providerMessageId]);

  const totalBytes = useMemo(
    () => attachments.reduce((s, f) => s + (f.size || 0), 0),
    [attachments],
  );

  const finalHtml = useMemo(() => {
    if (mode === "forward" || !includeSignature || !signatureHtml) {
      return messageHtml;
    }
    return appendSignature(messageHtml, signatureHtml);
  }, [messageHtml, includeSignature, signatureHtml, mode]);

  const bodyHasContent = stripHtml(finalHtml).length > 0;
  const canSend = !!accountId && to.length > 0 && (!!subject.trim() || mode !== "new")
    && bodyHasContent && !pendingDraft;

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const next = [...attachments, ...list];
    const nextTotal = next.reduce((s, f) => s + (f.size || 0), 0);
    if (nextTotal > MAX_TOTAL_BYTES) {
      toast.error(`Attachments exceed ${fmtBytes(MAX_TOTAL_BYTES)} cap`);
      e.target.value = "";
      return;
    }
    setAttachments(next);
    e.target.value = "";
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSend) return;
    setError("");

    const html = finalHtml;
    const text = stripHtml(html);

    try {
      await send.mutateAsync({
        accountId,
        to,
        cc: cc.length ? cc : undefined,
        bcc: bcc.length ? bcc : undefined,
        subject,
        text,
        html,
        replyTo: mode === "forward" ? undefined : replyTo?.replyTo || undefined,
        leadId: leadId || undefined,
        attachments: attachments.length ? attachments : undefined,
      });
      toast.success(mode === "new" ? "Email sent" : "Message sent");
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Send failed");
    }
  };

  const submitScheduled = async () => {
    if (!canSend || !scheduledAt) return;
    setError("");
    const html = finalHtml;
    const text = stripHtml(html);
    try {
      await schedule.mutateAsync({
        accountId, to, cc: cc.length ? cc : undefined, bcc: bcc.length ? bcc : undefined,
        subject, text, html,
        replyTo: mode === "forward" ? undefined : replyTo?.replyTo || undefined,
        leadId: leadId || undefined,
        attachments: attachments.length ? attachments : undefined,
        scheduledAt: scheduledAt.toISOString(),
      });
      toast.success(`Email scheduled for ${scheduledAt.format("MMM D, YYYY h:mm A")}`);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Schedule failed");
    }
  };

  const toggleCc = () => setShowCc((v) => !v);
  const toggleBcc = () => setShowBcc((v) => !v);
  const showSigBlock = signatureHtml && mode !== "forward";

  // Thread behind this compose window, when there is one. Inbox nests the id
  // under replyTo.replyTo (the RFC reply headers); a lead-tab reply may put it
  // top-level; forwards carry the source message. Accept every shape.
  const threadIdForAssist =
    replyTo?.replyTo?.providerThreadId
    || replyTo?.providerThreadId
    || replyTo?.message?.providerThreadId
    || null;
  const isReplyMode = mode === "reply" || mode === "replyAll";

  // "Write with AI" works in every mode — with a thread it drafts a reply, and
  // without one it writes from the lead record plus the user's instruction.
  const aiAvailable = activeAccounts.length > 0;
  // SOP-grounded response POINTS still need a real vendor thread to read.
  const pointsAvailable = isReplyMode && !!accountId && !!threadIdForAssist;

  // Both live in one slide-out drawer so the compose editor keeps its full
  // width. Opened via the "AI Assist" button in the header.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTab, setAiTab] = useState("draft"); // "draft" | "points"
  // Reset the drawer whenever the modal reopens for a different message/mode.
  useEffect(() => { setAiOpen(false); setAiTab("draft"); }, [open, mode, threadIdForAssist]);

  // Drop an AI draft into the editor. `append` keeps whatever the user already
  // wrote and adds the draft underneath; otherwise the draft replaces the body.
  // A suggested subject only fills an empty Subject field — never overwrites.
  const applyDraft = ({ body, subject: draftSubject, append }) => {
    const html = textToComposeHtml(body);
    if (!html) return;
    setMessageHtml((prev) => (
      append && stripHtml(prev).trim() ? `${prev}<p><br></p>${html}` : html
    ));
    if (draftSubject && !subject.trim()) setSubject(draftSubject);
    setAiOpen(false);
    toast.success(append ? "Draft appended" : "Draft added to your email");
  };

  return (
    <>
    <Modal open={open} onClose={onClose} width={800}>
      <form onSubmit={submit} className="crm-compose-modal-form crm-compose-modal">
        <header className="crm-compose-modal-header">
          <div style={{ fontSize: 15, fontWeight: 600 }}>{MODE_LABEL[mode] || "New message"}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {aiAvailable && (
              <button
                type="button"
                onClick={() => setAiOpen((v) => !v)}
                title="Write this email with AI"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", fontSize: 13, fontWeight: 600,
                  borderRadius: 8, cursor: "pointer",
                  border: "1px solid transparent",
                  color: "#fff",
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.25)",
                }}
              >
                <Sparkles size={14} />
                AI Assist
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)" }}>
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="crm-compose-modal-body">
          {accountsLoading ? (
            <div style={{ padding: 24, display: "flex", gap: 8, alignItems: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
              <Loader2 size={14} className="animate-spin" /> Loading mailboxes…
            </div>
          ) : activeAccounts.length === 0 ? (
            <div style={{ margin: "16px 0", background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 12, borderRadius: 8, display: "flex", gap: 10, fontSize: 13 }}>
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <div>No connected mailbox. Go to <strong>Settings → Email</strong> to connect Gmail.</div>
            </div>
          ) : (
            <>
              <FieldRow label="From">
                {activeAccounts.length > 1 ? (
                  <select
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    className="crm-compose-field-input crm-compose-field-select"
                  >
                    {activeAccounts.map((a) => (
                      <option key={a._id} value={a._id}>{a.email}</option>
                    ))}
                  </select>
                ) : (
                  <div className="crm-compose-field-input crm-compose-field-static">
                    {selectedAccount?.email}
                  </div>
                )}
              </FieldRow>

              <FieldRow
                label="To"
                extra={(
                  <div style={{ display: "flex", gap: 8, paddingTop: 8 }}>
                    {!showCc && (
                      <button type="button" onClick={toggleCc} style={{ border: "none", background: "transparent", fontSize: 12, color: "var(--crm-primary)", cursor: "pointer", fontWeight: 600 }}>
                        Cc
                      </button>
                    )}
                    {!showBcc && (
                      <button type="button" onClick={toggleBcc} style={{ border: "none", background: "transparent", fontSize: 12, color: "var(--crm-primary)", cursor: "pointer", fontWeight: 600 }}>
                        Bcc
                      </button>
                    )}
                  </div>
                )}
              >
                <EmailChipInput value={to} onChange={setTo} onDraftChange={setDraftFor("to")} placeholder="Recipients" required />
              </FieldRow>

              {showCc && (
                <FieldRow
                  label="Cc"
                  extra={(
                    <button type="button" onClick={toggleCc} style={{ border: "none", background: "transparent", paddingTop: 10, cursor: "pointer", color: "var(--color-text-secondary)" }}>
                      <ChevronUp size={14} />
                    </button>
                  )}
                >
                  <EmailChipInput value={cc} onChange={setCc} onDraftChange={setDraftFor("cc")} placeholder="Cc recipients" />
                </FieldRow>
              )}

              {showBcc && (
                <FieldRow
                  label="Bcc"
                  extra={(
                    <button type="button" onClick={toggleBcc} style={{ border: "none", background: "transparent", paddingTop: 10, cursor: "pointer", color: "var(--color-text-secondary)" }}>
                      <ChevronUp size={14} />
                    </button>
                  )}
                >
                  <EmailChipInput value={bcc} onChange={setBcc} onDraftChange={setDraftFor("bcc")} placeholder="Bcc recipients" />
                </FieldRow>
              )}

              <FieldRow label="Subject">
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required={mode === "new" || mode === "forward"}
                  placeholder="Subject"
                  className="crm-compose-field-input"
                />
              </FieldRow>

              <div className="crm-compose-modal-editor-wrap">
                <EmailComposeEditor
                  value={messageHtml}
                  onChange={setMessageHtml}
                  placeholder="Write your message…"
                  variant="compose"
                  toolbarPosition="bottom"
                  autoFocus
                  allowImages
                  imageMaxWidth={480}
                />
              </div>

              {showSigBlock && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={includeSignature}
                      onChange={(e) => setIncludeSignature(e.target.checked)}
                    />
                    Include signature when sending
                  </label>
                  {includeSignature && <SignaturePreview html={signatureHtml} />}
                </div>
              )}

              {attachments.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 8 }}>
                    Attachments · {fmtBytes(totalBytes)}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {attachments.map((f, i) => (
                      <AttachmentChip key={`${f.name}-${i}`} file={f} onRemove={() => setAttachments((a) => a.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                </div>
              )}

              {!error && !canSend && (
                <div style={{ color: "var(--color-text-secondary)", fontSize: 12, marginBottom: 12 }}>
                  {pendingDraft
                    ? `Finish the address in ${pendingDraft.toUpperCase()} — press Enter to add it as a recipient.`
                    : to.length === 0
                      ? "Add at least one recipient in To before sending."
                      : !bodyHasContent
                        ? "Write a message before sending."
                        : "Add a subject before sending."}
                </div>
              )}

              {error && (
                <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: "8px 10px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
                  {error}
                </div>
              )}

              <input ref={fileInputRef} type="file" multiple onChange={onPickFiles} style={{ display: "none" }} />
            </>
          )}
        </div>

        <footer className="crm-compose-modal-footer" style={{ flexDirection: "column", alignItems: "stretch", gap: 0 }}>
          {/* Schedule picker row — shown when clock button is toggled */}
          {showSchedule && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0 10px", borderBottom: "1px solid var(--color-border-subtle)", marginBottom: 8 }}>
              <Clock size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", flexShrink: 0 }}>Send at</span>
              <DatePicker
                showTime={{ format: "h:mm A", use12Hours: true }}
                format="MMM D, YYYY h:mm A"
                value={scheduledAt}
                onChange={(val) => setScheduledAt(val)}
                disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
                disabledTime={(d) => {
                  if (!d || !d.isSame(dayjs(), "day")) return {};
                  const now = dayjs();
                  return {
                    disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                    disabledMinutes: (h) => h === now.hour() ? Array.from({ length: now.minute() + 1 }, (_, i) => i) : [],
                  };
                }}
                placeholder="Pick date & time"
                style={{ flex: 1, fontSize: 12 }}
                size="middle"
                getPopupContainer={(el) => el.closest(".crm-compose-modal") || document.body}
              />
              <button
                type="button"
                onClick={() => { setShowSchedule(false); setScheduledAt(null); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", padding: 2, display: "flex" }}
              >
                <X size={13} />
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {activeAccounts.length > 0 && (
              <Button type="button" variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={send.isPending || schedule.isPending}>
                <Paperclip size={14} />
                <span style={{ marginLeft: 6 }}>Attach</span>
              </Button>
            )}
            {/* Lead files picker — only shown when composing from within a lead */}
            {leadId && (
              <Button type="button" variant="ghost" onClick={() => setFilePickerOpen(true)} disabled={send.isPending || schedule.isPending}>
                <FolderOpen size={14} />
                <span style={{ marginLeft: 6 }}>Lead files</span>
              </Button>
            )}
            {/* Schedule toggle */}
            <button
              type="button"
              title="Schedule send"
              onClick={() => setShowSchedule((v) => !v)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "6px 10px", fontSize: 12, fontWeight: 600,
                background: showSchedule ? "var(--crm-primary-soft)" : "transparent",
                border: `1px solid ${showSchedule ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                borderRadius: 8, cursor: "pointer",
                color: showSchedule ? "var(--crm-primary)" : "var(--color-text-secondary)",
              }}
            >
              <Clock size={13} /> Schedule
            </button>
            <div style={{ flex: 1 }} />
            <Button type="button" variant="ghost" onClick={onClose}>Discard</Button>
            {/* Show "Send later" when a schedule time is set, otherwise normal Send */}
            {showSchedule && scheduledAt && dayjs.isDayjs(scheduledAt) ? (
              <Button
                type="button"
                disabled={!canSend || schedule.isPending}
                onClick={submitScheduled}
              >
                {schedule.isPending ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                <span style={{ marginLeft: 6 }}>Send later</span>
              </Button>
            ) : (
              <Button type="submit" disabled={!canSend || send.isPending}>
                {send.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                <span style={{ marginLeft: 6 }}>Send</span>
              </Button>
            )}
          </div>
        </footer>
      </form>

      {/* AI drawer — slide-out over the compose modal's right edge. Editor keeps
          full width; nothing generates until the user clicks inside. Two tabs:
          a full draft they can insert, and the SOP-grounded response points. */}
      <AnimatePresence>
        {aiAvailable && aiOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setAiOpen(false)}
              style={{ position: "absolute", inset: 0, background: "rgba(15,15,30,0.18)", zIndex: 5 }}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 38 }}
              style={{
                position: "absolute", top: 0, right: 0, bottom: 0,
                width: 400, maxWidth: "92%", zIndex: 6,
                background: "var(--color-bg-base)",
                borderLeft: "1px solid var(--color-border-subtle)",
                boxShadow: "-16px 0 48px rgba(0,0,0,0.18)",
                display: "flex", flexDirection: "column",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: "var(--color-text-primary)" }}>
                  <Sparkles size={15} color="#8b5cf6" /> AI assist
                </div>
                <button type="button" onClick={() => setAiOpen(false)} aria-label="Close assist" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)" }}>
                  <X size={16} />
                </button>
              </div>

              {/* Tabs — "Response points" only exists when there's a vendor
                  thread to read, so a new email shows the draft writer alone. */}
              {pointsAvailable && (
                <div style={{ display: "flex", gap: 4, padding: "10px 14px 0" }}>
                  {[
                    { key: "draft", label: "Write draft" },
                    { key: "points", label: "Response points" },
                  ].map((t) => {
                    const active = aiTab === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setAiTab(t.key)}
                        style={{
                          padding: "6px 12px", fontSize: 12, fontWeight: active ? 700 : 500,
                          borderRadius: 8, cursor: "pointer",
                          border: `1px solid ${active ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                          background: active ? "var(--crm-primary-soft)" : "transparent",
                          color: active ? "var(--crm-primary)" : "var(--color-text-secondary)",
                          transition: "all 120ms",
                        }}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Both panels stay mounted and are hidden rather than unmounted,
                  so flipping tabs never throws away a generated draft. */}
              <div style={{ flex: 1, overflowY: "auto", padding: 14, minHeight: 0 }}>
                <div style={{ display: aiTab === "points" && pointsAvailable ? "none" : "block", height: "100%" }}>
                  <EmailDraftPanel
                    accountId={accountId}
                    threadId={threadIdForAssist}
                    leadId={leadId || undefined}
                    mode={mode}
                    subject={subject}
                    recipients={to}
                    bodyHasContent={stripHtml(messageHtml).trim().length > 0}
                    onApply={applyDraft}
                  />
                </div>
                {pointsAvailable && (
                  <div style={{ display: aiTab === "points" ? "block" : "none" }}>
                    <EmailAssistPanel
                      accountId={accountId}
                      threadId={threadIdForAssist}
                      leadId={leadId || undefined}
                      compact
                      hideHeaderChrome
                    />
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </Modal>

    <LeadFilePicker
      leadId={leadId}
      open={filePickerOpen}
      onClose={() => setFilePickerOpen(false)}
      onAttach={(files) => {
        const next = [...attachments, ...files];
        const total = next.reduce((s, f) => s + (f.size || 0), 0);
        if (total > MAX_TOTAL_BYTES) {
          toast.error(`Attachments exceed ${fmtBytes(MAX_TOTAL_BYTES)} cap`);
          return;
        }
        setAttachments(next);
      }}
    />
    </>
  );
}
